import React from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, BadgeCheck, RotateCcw, ArrowUpRight } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import PointsCalculator from "@/components/PointsCalculator";
import { PUNTOS_EUR, CARNET_EUR } from "@/lib/courses";

const ITEMS = [
  { icon: BadgeCheck, title: "Centro autorizado DGT", text: "El centro de Plaza de América está homologado oficialmente para impartir estos cursos." },
  { icon: RotateCcw, title: "Sensibilización y reeducación vial", text: "Recupera los puntos del carnet o la licencia tras una pérdida total." },
];

const FACTS = [
  { value: "8", label: "puntos al sacar el carnet o al recuperarlo tras examinarte" },
  { value: "12", label: "puntos con experiencia (3 años)" },
  { value: "15", label: "puntos máximos por buen conductor" },
  { value: "+4", label: "puntos que recuperas en 1 día + 3 h online" },
];

export default function PuntosSection() {
  return (
    <section id="puntos" className="section-pad bg-white relative overflow-hidden" data-testid="puntos-section">
      <div className="absolute inset-0 faro-grid-bg opacity-40 pointer-events-none" />
      <div className="max-w-7xl mx-auto px-5 sm:px-8 relative">
        <Reveal className="max-w-3xl">
          <span className="eyebrow">Seguridad vial</span>
          <h2 className="font-display font-black tracking-tighter text-4xl sm:text-5xl mt-3 leading-tight flex items-center gap-3">
            <ShieldCheck className="text-[var(--faro-red)] shrink-0" size={40} /> Recuperación de puntos y del permiso
          </h2>
          <p className="mt-4 text-lg text-[var(--faro-text-soft)]">
            Cursos para recuperar hasta 4 puntos del carnet o la licencia y cursos de sensibilización y
            reeducación vial para recuperar el permiso tras la pérdida total de puntos. Impartidos en
            nuestro centro autorizado de Plaza de América (Vigo).
          </p>
        </Reveal>

        <Reveal delay={0.05}>
          <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-4">
            {FACTS.map((f) => (
              <div key={f.label} className="rounded-2xl bg-[var(--faro-blue)] text-white p-5" data-testid={`puntos-fact-${f.value}`}>
                <div className="font-display font-black text-4xl sm:text-5xl tracking-tighter text-[var(--faro-yellow)]">{f.value}</div>
                <div className="text-xs sm:text-sm text-white/80 mt-1 leading-snug">{f.label}</div>
              </div>
            ))}
          </div>
        </Reveal>

        <Reveal delay={0.08}>
          <div className="mt-6 rounded-2xl border border-[rgba(30,42,143,0.12)] bg-[var(--faro-bg)] p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <p className="text-[var(--faro-text-soft)]">
              <strong className="text-[var(--faro-text)]">¿Cómo funciona?</strong> Solo se pierden puntos por infracciones graves o muy graves.
              Los recuperas dejando pasar el tiempo sin sancionar o, mucho antes, con un curso homologado. Si te quedas a cero,
              necesitas el curso de recuperación total (20h) para volver a conducir.
            </p>
            <Link to="/blog/permiso-por-puntos-como-funciona" data-testid="puntos-blog-link" className="btn-pill btn-secondary text-sm shrink-0">
              Leer la guía completa <ArrowUpRight size={16} />
            </Link>
          </div>
        </Reveal>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mt-10">
          {ITEMS.map((it, i) => (
            <Reveal key={it.title} delay={i * 0.06}>
              <div className="h-full rounded-2xl border border-[rgba(30,42,143,0.12)] bg-white p-6 hover:-translate-y-1 hover:shadow-xl hover:shadow-[rgba(230,57,70,0.12)] transition-all" data-testid={`puntos-item-${i}`}>
                <div className="w-11 h-11 rounded-xl bg-[rgba(230,57,70,0.1)] text-[var(--faro-red)] flex items-center justify-center">
                  <it.icon size={22} />
                </div>
                <h3 className="font-display font-bold text-lg mt-4 text-[var(--faro-text)]">{it.title}</h3>
                <p className="text-sm text-[var(--faro-text-soft)] mt-2 leading-relaxed">{it.text}</p>
              </div>
            </Reveal>
          ))}
        </div>

        <PointsCalculator />

        <Reveal delay={0.08}>
          <div className="mt-10 rounded-3xl bg-[var(--faro-blue)] text-white p-7 sm:p-10 grid grid-cols-1 lg:grid-cols-12 gap-6 items-center" data-testid="puntos-home-cta">
            <div className="lg:col-span-8">
              <div className="text-xs uppercase tracking-[0.2em] font-bold text-[var(--faro-yellow)]">Centro autorizado DGT · Plaza América</div>
              <h3 className="font-display font-black text-2xl sm:text-3xl mt-2 leading-tight">
                Fechas, horarios, precios y reserva online
              </h3>
              <p className="mt-2 text-white/85">
                Consulta el calendario completo de convocatorias 2026, los horarios de cada curso
                (parcial <strong>1 día + 3 h online</strong> · {PUNTOS_EUR} y total 20 h · {CARNET_EUR}) y reserva tu plaza en dos minutos.
              </p>
            </div>
            <div className="lg:col-span-4 flex flex-col gap-3">
              <Link to="/recuperacion-de-puntos-vigo#puntos-formulario" className="btn-pill btn-primary justify-center" data-testid="puntos-home-cta-reserva">
                Reservar mi plaza <ArrowUpRight size={16} />
              </Link>
              <Link to="/recuperacion-de-puntos-vigo" className="btn-pill bg-white text-[var(--faro-blue)] justify-center" data-testid="puntos-home-cta-info">
                Fechas y requisitos
              </Link>
              <Link to="/proximos-cursos" className="text-center text-sm font-bold text-white/85 underline underline-offset-4 hover:text-white">
                Ver próximas convocatorias
              </Link>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
